import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "@ag-ui/client";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { EVAL_RUN_TERMINAL_STATUSES } from "~/pages/manifold-nextgen/types/eval-run";
import { apiClient } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;

// launch_eval_run is exposed through the analysis-agent's evals toolkit under
// the `evals` MCP prefix (cf. tools/evals.py `.prefixed("evals")`), so this is
// the name it carries in the turn's tool calls.
const LAUNCH_EVAL_RUN_TOOL_NAME = "evals_launch_eval_run";

export interface SessionEvalRun {
  // Eval run id, recovered from the launch_eval_run tool result.
  runId: string;
  // Case-selector tags from that same result — the human-facing label for the
  // TOC row and the detail switcher, so neither needs a poll. Empty when the run
  // targets every case.
  tags: string[];
}

interface SessionEvalRunsContextValue {
  evalRuns: SessionEvalRun[];
  setEvalRuns: (evalRuns: SessionEvalRun[]) => void;
}

// Holds the session's eval-run list above both `Chat` (which owns the messages
// it is derived from) and `SessionContextSidebar` (which renders it) — the two
// are siblings, so the shared state lives in their common ancestor.
// Mirrors `SessionIngestsContext`.
const SessionEvalRunsContext = createContext<SessionEvalRunsContextValue | null>(null);

export const SessionEvalRunsProvider = ({ children }: { children: React.ReactNode }) => {
  const [evalRuns, setEvalRuns] = useState<SessionEvalRun[]>([]);

  return (
    <SessionEvalRunsContext.Provider value={{ evalRuns, setEvalRuns }}>
      {children}
    </SessionEvalRunsContext.Provider>
  );
};

// Recover the run id and case-selector tags from a launch_eval_run tool result.
// The tool returns an EvalRun dict, so both live at the top level:
// `JSON.parse(result).id` and `.tags`.
function parseEvalRunResult(raw: string): { id: string; tags: string[] } | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const rec = parsed as { id?: unknown; tags?: unknown };
      if (typeof rec.id === "string" && rec.id.length > 0) {
        const tags = Array.isArray(rec.tags)
          ? rec.tags.filter((t): t is string => typeof t === "string")
          : [];
        return { id: rec.id, tags };
      }
    }
  } catch {
    // Not JSON — nothing to recover.
  }
  return null;
}

// Membership is a pure function of conversation history: every launch_eval_run
// result across all turns contributes its run id, de-duped, in first-seen order.
// The conversations service is never consulted here; it supplies only per-row
// state downstream, via useEvalRunStatus.
export function sessionEvalRunsFromMessages(messages: ReadonlyArray<Message>): SessionEvalRun[] {
  const resultByCallId = new Map<string, string>();
  for (const m of messages) {
    const tm = m as { role?: string; toolCallId?: string; content?: string };
    if (tm.role === "tool" && tm.toolCallId && typeof tm.content === "string") {
      resultByCallId.set(tm.toolCallId, tm.content);
    }
  }

  const seen = new Set<string>();
  const evalRuns: SessionEvalRun[] = [];
  for (const m of messages) {
    const calls = (m as { toolCalls?: { id: string; function: { name: string } }[] }).toolCalls;
    if (!calls) {
      continue;
    }
    for (const tc of calls) {
      if (tc.function.name !== LAUNCH_EVAL_RUN_TOOL_NAME) {
        continue;
      }
      const raw = resultByCallId.get(tc.id);
      if (raw === undefined) {
        continue;
      }
      const parsed = parseEvalRunResult(raw);
      if (!parsed || seen.has(parsed.id)) {
        continue;
      }
      seen.add(parsed.id);
      evalRuns.push({ runId: parsed.id, tags: parsed.tags });
    }
  }
  return evalRuns;
}

async function isEvalRunInFlight(runId: string, signal: AbortSignal): Promise<boolean> {
  const resp = await apiClient<{ status: string }>(`${VITE_CONVERSATIONS_API}/eval-runs/${runId}`, {
    signal,
  });
  return !EVAL_RUN_TERMINAL_STATUSES.has(resp.status);
}

// Called from `Chat`. Publishes the derived membership into the shared context
// and drives the panel's open/focus:
//   - a launch_eval_run that appears *after* mount is a live event → open + focus
//     its row immediately (the streamed result is the signal);
//   - ids already present at mount are a reload → focus the most recent one only
//     if a one-shot status poll shows it is still in flight, otherwise stay
//     passive. The list itself stays visible in the TOC either way.
export function useSessionEvalRunsSync(messages: ReadonlyArray<Message>): void {
  const ctx = useContext(SessionEvalRunsContext);
  const { openPanel } = useSessionContextPanel();
  const seenRef = useRef<Set<string>>(new Set());
  const mountCapturedRef = useRef(false);

  // `GET /eval-runs/{id}` is MANAGE_AGENTS-gated, so a viewer without that role
  // gets an error on every poll. Rather than render rows that never resolve, we
  // gate the whole feature here: no runs are published, so the TOC entry, the
  // list, and the auto-open all fall away. This is the one place the eval-run
  // pattern diverges from ingests, whose read endpoint is not role-gated.
  const canManage = useCanManageAIAgents();

  const evalRuns = useMemo(
    () => (canManage ? sessionEvalRunsFromMessages(messages) : []),
    [messages, canManage]
  );

  // Bridge the derived list into the shared context so the sibling sidebar/TOC
  // can render it. (Cross-tree sharing: the consumers are siblings of `Chat`.)
  useEffect(() => {
    ctx?.setEvalRuns(evalRuns);
  }, [ctx, evalRuns]);

  useEffect(() => {
    // First pass: everything already present is the reload set. Record it
    // without opening, then focus the most recent only if it's still running.
    if (!mountCapturedRef.current) {
      mountCapturedRef.current = true;
      evalRuns.forEach((r) => seenRef.current.add(r.runId));
      const last = evalRuns[evalRuns.length - 1];
      if (!last) {
        return;
      }
      const controller = new AbortController();
      isEvalRunInFlight(last.runId, controller.signal)
        .then((inFlight) => {
          if (!controller.signal.aborted && inFlight) {
            openPanel(SessionContextPanelType.EVAL_RUN, last.runId);
          }
        })
        .catch(() => {
          // Status unknown (transient failure / run gone) → don't steal focus;
          // the row still shows in the TOC and its own poll will catch up.
        });
      return () => controller.abort();
    }

    // Later passes: a genuinely new id is a live launch_eval_run → open + focus
    // it (last one wins if several land at once).
    const fresh = evalRuns.filter((r) => !seenRef.current.has(r.runId));
    if (fresh.length === 0) {
      return;
    }
    fresh.forEach((r) => seenRef.current.add(r.runId));
    openPanel(SessionContextPanelType.EVAL_RUN, fresh[fresh.length - 1].runId);
  }, [evalRuns, openPanel]);
}

// Read-only projection of the session's eval-run list — what the sidebar's
// Eval runs section and detail view render from.
export function useSessionEvalRuns(): SessionEvalRun[] {
  const ctx = useContext(SessionEvalRunsContext);
  if (!ctx) {
    throw new Error("useSessionEvalRuns must be used within a SessionEvalRunsProvider");
  }
  return ctx.evalRuns;
}
